import { Logger, LoggerFactory } from './../../../core/logger.service';
import { LanguageService } from './../../../core/language.service';
import { Component, EventEmitter, Input, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SiteSpecific, UserSession } from '../../../core/site-specific.base';
import { filter } from 'rxjs/operators';
import { ThemeService } from 'app/shared/services/theme.service';
import { Subscription } from 'rxjs';
import { TemplateType } from 'app/campaign/templates/template.model';
import { FlyoutService } from 'app/flyout/flyout.service';
import { FlyoutDataType, FlyoutOpeningData } from 'app/flyout/flyout-models';
import { ReferralTypes } from 'app/campaign/campaign.model';
import { Account } from 'app/back-office/account.model';
import { TierAccess } from 'app/shared/account/tier.model';

@Component({
  selector: 'navigation-item',
  templateUrl: './navigation-item.component.html',
  styleUrls: ['navigation-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NavigationItemComponent extends SiteSpecific {
  private readonly logger: Logger;
  public isSelected: boolean;

  public get isHighlighted(): boolean {
    return this.isSelected && (!this.highlightExactMatchOnly || this.activeUrl === this.url);
  }
  public activeUrl: string;
  public label: string;
  public isDarkThemeSub: Subscription;
  public isDarkTheme: boolean;
  public TemplateType = TemplateType;
  public tierAccess: TierAccess;

  @Output() public selecting = new EventEmitter<NavigationItemComponent>();
  @Input() public url: string;
  @Input() public clickable: boolean;
  @Input() public highlightExactMatchOnly = false;
  @Input() public icon: string;
  @Input() public materialIcon: string;
  @Input() public translationKey: string;
  @Input() public color = '#E7E7EF';
  @Input() public isBetaItem = false;
  @Input() public showCampaignCreateButton = false;
  @Input() public campaignCreateRouterLink: string;
  @Input() public campaignCreateRouterParam: string;
  @Input() public isColor: boolean;
  @Input() public currentAccount: Account;
  @Input() public adminOnly: boolean;
  @Input() public isNew: boolean;
  @Input() public showUpgradeTag: boolean;
  @Input() public dataTest: string;

  constructor(
    private languageService: LanguageService,
    protected userSession: UserSession,
    private readonly themeService: ThemeService,
    private router: Router,
    protected loggerFactory: LoggerFactory,
    protected readonly flyoutService: FlyoutService,
  ) {
    super(userSession, loggerFactory);
    this.logger = loggerFactory.get('NavigationItemComponent');
  }

  protected async onSiteChanged() {
    this.tierAccess = new TierAccess(this.currentAccount?.tier);
    this.ensureInputs();
    await this.initTitleService();
    this.label = await this.languageService.translateAsync(this.translationKey);
    this.isDarkThemeSub = this.themeService.isDarkThemeBS.subscribe((item) => {
      this.isDarkTheme = item;
    });
  }

  private async initTitleService() {
    await this.handleRouteChange();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async () => await this.handleRouteChange());
  }

  private async handleRouteChange() {
    if (this.clickable) {
      this.isSelected = false;
      return;
    }
    // TODO: refactor routes so subpages match routes
    this.activeUrl = this.router.routerState.snapshot.url;
    if (this.url === '/' && this.activeUrl === '/') {
      // Root page
      this.isSelected = true;
      this.selecting.emit(this);
      return;
    } else {
      // Subpages
      const routeFirstPart = '/' + this.url.split('/')[1];
      if (
        this.activeUrl.startsWith(routeFirstPart) &&
        routeFirstPart !== '/' &&
        !this.activeUrl.startsWith('/insights') &&
        !this.activeUrl.startsWith('/backoffice')
      ) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (
        this.url === '/audiences' &&
        (this.activeUrl.startsWith('/profiles') || this.activeUrl.startsWith('/insights'))
      ) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (this.url === this.activeUrl) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (
        this.url === '/backoffice/campaigns/templates' &&
        this.activeUrl.startsWith('/backoffice/campaigns/')
      ) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (
        this.url === '/backoffice/email-templates' &&
        this.activeUrl.startsWith('/backoffice/email-templates')
      ) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (
        this.url === '/backoffice/campaign-classification' &&
        this.activeUrl.startsWith('/backoffice/campaign-classification')
      ) {
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      } else if (
        (this.url === '/backoffice/customers' ||
          this.url === '/backoffice/accounts' ||
          this.url === '/backoffice/users') &&
        (this.activeUrl === this.url || this.activeUrl.startsWith(this.url + '/'))
      ) {
        // Primary backoffice items stay highlighted on their detail subpages.
        this.isSelected = true;
        this.selecting.emit(this);
        return;
      }
    }
    this.isSelected = false;
  }

  private ensureInputs() {
    if (!this.url && !this.clickable) {
      this.logger.error(`No url is given for the item.\nThis:${this.toString()}`);
    }
    if (!this.icon && !this.materialIcon) {
      this.logger.error(`No icon is given for the item.\nThis:${this.toString()}`);
    }
    if (!this.translationKey) {
      this.logger.error(`No translationKey is given for the item.\nThis:${this.toString()}`);
    }
    if (!this.color) {
      this.logger.error(`No color is given for the item.\nThis:${this.toString()}`);
    }
  }

  private toString(): string {
    return `Url: "${this.url}"–Icon: "${this.icon || this.materialIcon}"–Translation key:"${
      this.translationKey
    }"–Color:"${this.color}"`;
  }

  routeToTemplatePage() {
    this.router.navigate(['/campaigns/create/templates'], { queryParams: { type: this.campaignCreateRouterParam } });
  }
}
